
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import api from "@/services/api"
import { DateTime } from "luxon"

import TimeEntry from "./TimeEntry.vue"

import { TimeEntry as TimeEntryType, TimeEntryInputFormat } from "@/types/timeEntry"

@Component({
	components: {
		"time-entry": TimeEntry,
		"date-time": () => import("@/components/Fields/DateTime.vue"),
		"time-duration-picker": () => import("./CaseDetails/Users/TimeDurationPicker.vue")
	}
})
export default class TimeEntries extends Vue {
	/* --- Props --- */

	@Prop({ type: Array, required: true })
	value!: TimeEntryType[]

	@Prop({ type: Number, required: true })
	timeSpent!: number

	@Prop({ type: String, default: TimeEntryInputFormat.Decimal })
	inputFormat!: TimeEntryInputFormat

	@Prop({ type: Boolean, default: false })
	isReadonly!: boolean

	/* --- Data --- */

	TimeEntryInputFormat = TimeEntryInputFormat

	timeEntries: TimeEntryType[] = [...this.value]
	createDate: Date | null = null
	createHours = 0

	/* --- Watchers --- */

	@Watch("value", { immediate: true, deep: true })
	onTimeEntriesChange (newVal: TimeEntryType[]) {
		this.timeEntries = newVal.map(entry => ({
			...entry,
			date: typeof entry.date === "string" ? new Date(entry.date) : entry.date
		}))
	}

	/* --- Computed Properties --- */

	private get sortedTimeEntries () {
		return this.sortTimeEntries(this.timeEntries)
	}

	/* --- Methods --- */

	/**
	 * Sorts an array of time entries by date.
	 * @param entries The array of time entries to sort.
	 * @param descending If true, sorts in descending order; otherwise ascending.
	 * @returns Sorted array of time entries.
	 */
	private sortTimeEntries (entries: TimeEntryType[], descending = false): TimeEntryType[] {
		return [...entries].sort((a, b) =>
			descending
				? b.date.getTime() - a.date.getTime()
				: a.date.getTime() - b.date.getTime()
		)
	}

	/**
	 * Cancels the creation of a new time entry and resets related fields.
	 */
	private cancelNewTimeEntry () {
		this.createDate = null
		this.createHours = 0
	}

	/**
	 * Sets up a new time entry date.
	 * If no entries exist, sets to current date (UTC midnight).
	 * Otherwise, sets to one day after the latest entry.
	 */
	private setupNewTimeEntry () {
		if (this.timeEntries.length === 0) {
			const now = DateTime.now().startOf("day") // local start of day
			this.createDate = (DateTime as any).fromObject(
				{
					year: now.year,
					month: now.month,
					day: now.day,
					hour: 0,
					minute: 0,
					second: 0
				}, { zone: "utc" }
			).toJSDate()
			return
		}

		const latestDate = this.timeEntries
			.map(entry => entry.date)
			.reduce((latest, curr) => curr > latest ? curr : latest)
		const nextDate = new Date(latestDate)
		nextDate.setUTCDate(nextDate.getUTCDate() + 1)
		nextDate.setUTCHours(0, 0, 0, 0)
		this.createDate = nextDate
	}

	/**
	 * Adds a new time entry with the selected date and time.
	 * If an entry for the date exists, increments its time.
	 * Emits `input` event to notify parent components.
	 */
	private async addNewTimeEntry () {
		if (!this.createDate || this.createHours < 0) return

		const existingEntry = this.timeEntries.find(entry => {
			const entryDate = entry.date
			return entryDate.getTime() === this.createDate!.getTime()
		})

		if (existingEntry) {
			existingEntry.time += this.createHours
			this.$emit("input", [...this.timeEntries])
			this.createDate = null
			this.createHours = 0
			return
		}

		const newId = await api.generateID()
		const newEntry: TimeEntryType = {
			id: newId,
			date: this.createDate!,
			time: this.createHours
		}

		this.timeEntries.push(newEntry)
		this.$emit("input", [...this.timeEntries])

		this.createDate = null
		this.createHours = 0
	}

	/**
	 * Removes a time entry by its ID and emits an `input` event to notify parent components.
	 * @param id The ID of the time entry to remove.
	 */
	private removeTimeEntry (id: string) {
		const index = this.timeEntries.findIndex(entry => entry.id === id)
		if (index !== -1) {
			this.timeEntries.splice(index, 1)
			this.$emit("input", [...this.timeEntries])
		}
	}

	/**
	 * Updates an existing time entry and emits an "input" event to notify parent components.
	 * @param updatedEntry The updated time entry object.
	 */
	private updateTimeEntry (updatedEntry: TimeEntryType) {
		const index = this.timeEntries.findIndex(entry => entry.id === updatedEntry.id)
		if (index !== -1) {
			this.timeEntries[index] = { ...updatedEntry }
			this.$emit("input", [...this.timeEntries])
		}
	}

	/**
	 * Increases the time for today's entry or creates a new entry for today if it doesn't exist.
	 * Emits `input` event to notify parent components.
	 * @param time The amount of time to add.
	 */
	private async applyTimeIncrease (time: number) {
		const now = DateTime.now().startOf("day") // local start of day
		const today = (DateTime as any).fromObject(
			{
				year: now.year,
				month: now.month,
				day: now.day,
				hour: 0,
				minute: 0,
				second: 0
			}, { zone: "utc" }
		).toJSDate()

		const existingIndex = this.timeEntries.findIndex(entry => {
			const entryDate = entry.date
			return entryDate.getTime() === today.getTime()
		})

		if (existingIndex !== -1) {
			this.timeEntries[existingIndex].time += time
		} else {
			const newId = await api.generateID()
			this.timeEntries.push({
				id: newId,
				date: today,
				time: time
			})
		}

		this.$emit("input", [...this.timeEntries])
	}

	/**
	 * Reduces time from the latest entries until the specified amount is removed.
	 * Entries with zero time are removed.
	 * @param timeToRemove The total amount of time to remove.
	 */
	private reduceTimeEntries (timeToRemove: number) {
		const sortedEntries = this.sortTimeEntries(this.timeEntries, true)

		for (const entry of sortedEntries) {
			if (timeToRemove === 0) break

			if (entry.time <= timeToRemove) {
				timeToRemove -= entry.time
				entry.time = 0
			} else {
				entry.time -= timeToRemove
				timeToRemove = 0
			}
		}

		this.timeEntries = sortedEntries.filter(entry => entry.time > 0) // Remove entries with 0 time
		this.$emit("input", [...this.timeEntries])
	}

	/**
	 * Applies a change to the total time spent by adjusting the time entries accordingly.
	 * If the new value is greater, it adds time to today's entry (or creates one if none exists).
	 * If the new value is smaller, it subtracts time starting from the most recent entries.
	 * If no entries exist but `timeSpent` is already set (legacy data), adds the new time spent to todays entry.
	 * @param newTimeSpent The new total time spent value to apply.
	 */
	public async applyTimeSpentChange (newTimeSpent: number) {
		let diff = newTimeSpent - this.timeSpent

		// Handle legacy data where `timeSpent` is set but timeEntries are missing
		if (this.timeEntries.length === 0 && this.timeSpent > 0) {
			diff = newTimeSpent
		}

		if (diff > 0) {
			await this.applyTimeIncrease(diff)
		} else if (diff < 0) {
			this.reduceTimeEntries(Math.abs(diff))
		}
	}
}
